import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import RallyPinCard from '@/components/rally/RallyPinCard';
import type { RallyPin } from '@/components/rally/RallyPinCard';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
const PIN_COLORS = ['red', 'blue', 'green', 'yellow', 'purple'];

type FilterType = 'all' | 'teacher' | 'prospect';

export default function RallyPinboard() {
  const { session } = useAuth();
  const token = session?.access_token || '';

  const [pins, setPins] = useState<RallyPin[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');

  // Pin form state
  const [myPin, setMyPin] = useState<RallyPin | null>(null);
  const [message, setMessage] = useState('');
  const [pinColor, setPinColor] = useState('red');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState('');
  const [isNominee, setIsNominee] = useState<boolean | null>(null);

  // Fetch pins
  const loadPins = useCallback(async () => {
    try {
      const res = await fetch(`${CORE_API}/api/v1/rally/pins?type=${filter}`);
      const json = await res.json();
      setPins(json.data?.pins || []);
    } catch {
      setPins([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadPins(); }, [loadPins]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(loadPins, 60000);
    return () => clearInterval(interval);
  }, [loadPins]);

  // Check if current user is a nominee (can post)
  useEffect(() => {
    if (!token) { setIsNominee(false); return; }

    fetch(`${CORE_API}/api/v1/rally/pins/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => {
        setMyPin(json.data?.pin || null);
        setIsNominee(true); // If the endpoint doesn't 403, they can try posting
      })
      .catch(() => setIsNominee(false));
  }, [token]);

  // Post a pin
  const handlePost = async () => {
    if (!message.trim() || posting) return;
    setPosting(true);
    setPostError('');
    try {
      const res = await fetch(`${CORE_API}/api/v1/rally/pins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: message.trim(), pin_color: pinColor }),
      });
      const json = await res.json();
      if (!res.ok) {
        setPostError(json.error || 'Failed to post');
        if (res.status === 403) setIsNominee(false);
        return;
      }
      setMyPin(json.data);
      setMessage('');
      loadPins();
    } catch {
      setPostError('Network error');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-b from-amber-50 to-transparent px-4 pt-8 pb-4">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-between mb-2">
            <Link to="/nominate" className="text-xs text-cocoa/50 hover:text-cocoa">&larr; Nominations</Link>
          </div>
          <h1 className="text-3xl font-bold text-cocoa">The Rally Board</h1>
          <p className="mt-1 text-sm text-cocoa/60">
            Nominees pin their campaigns here. Support your favorites to boost their chances in the next draw.
          </p>

          {/* Filter tabs */}
          <div className="mt-4 flex gap-1 rounded-lg bg-sand/30 p-1 w-fit">
            {(['all', 'teacher', 'prospect'] as FilterType[]).map((f) => (
              <button
                key={f}
                onClick={() => { setFilter(f); setLoading(true); }}
                className={`rounded-md px-4 py-1.5 text-xs font-medium transition-colors ${
                  filter === f ? 'bg-white text-cocoa shadow-sm' : 'text-cocoa/60 hover:text-cocoa'
                }`}
              >
                {f === 'all' ? 'All' : f === 'teacher' ? 'Teachers' : 'Prospects'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Cork Board */}
      <div
        className="min-h-[60vh] px-4 py-6"
        style={{
          background: 'linear-gradient(135deg, #C4A882 0%, #B89B71 25%, #C9AD85 50%, #BFA178 75%, #C4A882 100%)',
          backgroundSize: '200px 200px',
          boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <div className="mx-auto max-w-5xl">
          {/* Post form — only for confirmed nominees */}
          {token && isNominee && (
            <div className="mb-6 rounded-xl bg-white/90 backdrop-blur p-4 shadow-md border border-sand/50">
              {myPin ? (
                <div className="text-center">
                  <p className="text-sm text-cocoa/70">Your pin is live on the board!</p>
                  <p className="text-xs text-cocoa/50 mt-1">It expires in {Math.ceil(Math.max(0, (new Date(myPin.expires_at).getTime() - Date.now()) / (1000 * 60 * 60)))} hours. Post again to replace it.</p>
                </div>
              ) : null}

              <div className="flex gap-3 mt-2">
                <div className="flex-1">
                  <textarea
                    className="w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm text-cocoa resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
                    rows={2}
                    maxLength={280}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Write your campaign message (280 chars)..."
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-cocoa/50">Pin color:</span>
                      {PIN_COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setPinColor(c)}
                          className={`h-5 w-5 rounded-full transition-transform ${pinColor === c ? 'ring-2 ring-offset-1 ring-cocoa/40 scale-110' : 'hover:scale-110'}`}
                          style={{ backgroundColor: c === 'red' ? '#DC2626' : c === 'blue' ? '#2563EB' : c === 'green' ? '#059669' : c === 'yellow' ? '#D97706' : '#7C3AED' }}
                        />
                      ))}
                    </div>
                    <span className="text-[10px] text-cocoa/40">{message.length}/280</span>
                  </div>
                </div>
                <button
                  onClick={handlePost}
                  disabled={posting || !message.trim()}
                  className="self-start rounded-xl bg-brick px-4 py-2 text-sm font-semibold text-white transition hover:bg-brickDark disabled:opacity-50"
                >
                  {posting ? 'Pinning...' : 'Pin It'}
                </button>
              </div>
              {postError && <p className="text-xs text-red-500 mt-1">{postError}</p>}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/60 border-t-white" />
            </div>
          )}

          {/* Empty state */}
          {!loading && pins.length === 0 && (
            <div className="rounded-xl bg-white/80 p-10 text-center shadow-sm">
              <p className="text-lg font-semibold text-cocoa/60">No pins yet!</p>
              <p className="text-sm text-cocoa/40 mt-1">
                Confirmed nominees can post their campaign pins here.
              </p>
            </div>
          )}

          {/* Masonry grid */}
          {!loading && pins.length > 0 && (
            <div className="columns-1 gap-5 sm:columns-2 lg:columns-3 xl:columns-4">
              {pins.map((pin) => (
                <RallyPinCard key={pin.id} pin={pin} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-gradient-to-t from-amber-50 to-transparent px-4 py-8 text-center">
        <p className="text-sm text-cocoa/60">Know someone who deserves support?</p>
        <Link to="/nominate" className="mt-2 inline-block rounded-xl bg-brick px-5 py-2 text-sm font-semibold text-white transition hover:bg-brickDark">
          Nominate Someone
        </Link>
      </div>
    </div>
  );
}
